import { useCallback, useEffect, useRef } from 'react';

import { offerViewsRoute } from '../models/routes';
import { csrfTokenHeader } from '../services/CsrfTokenService';

const VIEW_THRESHOLD = 0.5;
const VIEW_DELAY_MS = 600;
const FLUSH_DELAY_MS = 250;

const viewedOfferIds = new Set<number>();
const pendingOfferIds = new Set<number>();
let flushTimer: number | null = null;

const flushOfferViews = () => {
    flushTimer = null;

    const offerIds = Array.from(pendingOfferIds);
    pendingOfferIds.clear();

    if (!offerIds.length) {
        return;
    }

    void fetch(offerViewsRoute(), {
        method: 'POST',
        keepalive: true,
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            ...csrfTokenHeader(),
        },
        body: JSON.stringify({ offer_ids: offerIds }),
    }).catch(() => {});
};

const queueOfferView = (offerId: number) => {
    if (viewedOfferIds.has(offerId)) {
        return;
    }

    viewedOfferIds.add(offerId);
    pendingOfferIds.add(offerId);

    if (flushTimer !== null) {
        return;
    }

    flushTimer = window.setTimeout(flushOfferViews, FLUSH_DELAY_MS);
};

const isMeaningfullyVisible = (entry: IntersectionObserverEntry) => {
    const visibleHeightRatio =
        entry.boundingClientRect.height > 0
            ? entry.intersectionRect.height / entry.boundingClientRect.height
            : entry.intersectionRatio;

    return (
        entry.isIntersecting &&
        (entry.intersectionRatio >= VIEW_THRESHOLD ||
            visibleHeightRatio >= VIEW_THRESHOLD)
    );
};

export const useOfferViewTracking = <TElement extends HTMLElement>() => {
    const observerRef = useRef<IntersectionObserver | null>(null);
    const timersRef = useRef<Map<Element, number>>(new Map());
    const observedElementsRef = useRef<Set<Element>>(new Set());

    useEffect(() => {
        return () => {
            observerRef.current?.disconnect();
            timersRef.current.forEach((timerId) =>
                window.clearTimeout(timerId),
            );
            timersRef.current.clear();
            observedElementsRef.current.clear();
        };
    }, []);

    return useCallback((element: TElement | null) => {
        if (!element || observedElementsRef.current.has(element)) {
            return;
        }

        const offerId = Number(element.dataset.offerId);
        if (!Number.isSafeInteger(offerId) || viewedOfferIds.has(offerId)) {
            return;
        }

        if (typeof IntersectionObserver === 'undefined') {
            queueOfferView(offerId);
            return;
        }

        if (!observerRef.current) {
            observerRef.current = new IntersectionObserver(
                (entries, observer) => {
                    entries.forEach((entry) => {
                        const target = entry.target as HTMLElement;
                        const targetOfferId = Number(target.dataset.offerId);
                        const existingTimer = timersRef.current.get(target);

                        if (
                            !Number.isSafeInteger(targetOfferId) ||
                            viewedOfferIds.has(targetOfferId)
                        ) {
                            if (existingTimer) {
                                window.clearTimeout(existingTimer);
                                timersRef.current.delete(target);
                            }

                            observer.unobserve(target);
                            observedElementsRef.current.delete(target);
                            return;
                        }

                        if (isMeaningfullyVisible(entry)) {
                            if (existingTimer) {
                                return;
                            }

                            const timerId = window.setTimeout(() => {
                                queueOfferView(targetOfferId);
                                timersRef.current.delete(target);
                                observer.unobserve(target);
                                observedElementsRef.current.delete(target);
                            }, VIEW_DELAY_MS);

                            timersRef.current.set(target, timerId);
                            return;
                        }

                        if (existingTimer) {
                            window.clearTimeout(existingTimer);
                            timersRef.current.delete(target);
                        }
                    });
                },
                { threshold: [0, VIEW_THRESHOLD] },
            );
        }

        observedElementsRef.current.add(element);
        observerRef.current.observe(element);
    }, []);
};
